import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "bottom-sheet" }
const _hoisted_2 = { class: "bottom-sheet-content" }
const _hoisted_3 = { class: "header-section" }
const _hoisted_4 = { class: "subtitle" }
const _hoisted_5 = { class: "step-indicators" }
const _hoisted_6 = {
  key: 0,
  class: "form-step"
}
const _hoisted_7 = { class: "form-row" }
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = {
  key: 0,
  class: "error-message"
}
const _hoisted_10 = { class: "form-group" }
const _hoisted_11 = {
  key: 0,
  class: "error-message"
}
const _hoisted_12 = {
  key: 1,
  class: "form-step"
}
const _hoisted_13 = { class: "form-group" }
const _hoisted_14 = {
  key: 0,
  class: "error-message"
}
const _hoisted_15 = { class: "form-group" }
const _hoisted_16 = {
  key: 0,
  class: "error-message"
}
const _hoisted_17 = {
  key: 2,
  class: "form-step"
}
const _hoisted_18 = { class: "form-group" }
const _hoisted_19 = { class: "form-group" }
const _hoisted_20 = { class: "form-actions" }
const _hoisted_21 = ["disabled"]

import { ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import { collection, addDoc } from 'firebase/firestore';
import { db, contactDocument, sendNotification } from '@/lib/firebase';
import { trackEvent } from '@/utils/analytics';
import { FIREBASE_COLLECTIONS, FIREBASE_SUBCOLLECTIONS } from '@/lib/constants';
import '@/assets/styles/ContactBottomSheet.css';

type Props = {
  show: boolean;
  recipientName: string;
  cardShareId: string;
  fcmToken?: string | null;
  documentId: string;
  companyId?: string;
};

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  timestamp: Date;
};

type FormErrors = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type ContactData = FormData & {
  cardShareId: string;
  status: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactBottomSheet',
  props: {
    show: { type: Boolean },
    recipientName: {},
    cardShareId: {},
    fcmToken: { default: null },
    documentId: {},
    companyId: {}
  },
  emits: ["close", "submit-success", "submit-error"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// State
const currentStep = ref(1);
const isSubmitting = ref(false);
const formData = ref<FormData>({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  title: '',
  timestamp: new Date(),
});

const formErrors = ref<FormErrors>({
  firstName: '',
  lastName: '',
  email: '',
  phone: ''
});

// Functions
const clearErrors = (): void => {
  formErrors.value = {
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  };
};

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  let formatted = phone.replace(/[^\d+]/g, '');
  if (formatted.includes('+')) {
    formatted = '+' + formatted.replace(/\+/g, '');
  }
  return formatted.slice(0, 16);
};

const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?\d{8,15}$/;
  return phoneRegex.test(phone);
};

// Watch phone number changes
watch(() => formData.value.phone, (newValue) => {
  if (newValue && newValue !== formatPhoneNumber(newValue)) {
    formData.value.phone = formatPhoneNumber(newValue);
  }
});

const nextStep = (): void => {
  clearErrors();

  if (currentStep.value === 1) {
    if (!formData.value.firstName.trim()) {
      formErrors.value.firstName = 'First name is required';
      return;
    }
    if (!formData.value.lastName.trim()) {
      formErrors.value.lastName = 'Last name is required';
      return;
    }
  }

  if (currentStep.value === 2) {
    if (!formData.value.email.trim()) {
      formErrors.value.email = 'Email is required';
      return;
    }
    if (!formData.value.phone.trim()) {
      formErrors.value.phone = 'Phone number is required';
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.value.email)) {
      formErrors.value.email = 'Please enter a valid email address';
      return;
    }

    if (!validatePhoneNumber(formData.value.phone)) {
      formErrors.value.phone = 'Please enter a valid phone number (minimum 8 digits)';
      return;
    }
  }

  if (currentStep.value < 3) {
    currentStep.value++;
  }
};

const previousStep = (): void => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const closeSheet = (): void => {
  currentStep.value = 1;
  emit('close');
};



const handleSubmit = async (): Promise<void> => {
  if (isSubmitting.value) return;

  clearErrors();

  // Validation checks
  if (!formData.value.firstName.trim()) {
    formErrors.value.firstName = 'First name is required';
    currentStep.value = 1;
    return;
  }

  if (!formData.value.lastName.trim()) {
    formErrors.value.lastName = 'Last name is required';
    currentStep.value = 1;
    return;
  }

  if (!formData.value.email.trim()) {
    formErrors.value.email = 'Email is required';
    currentStep.value = 2;
    return;
  }

  if (!formData.value.phone.trim()) {
    formErrors.value.phone = 'Phone number is required';
    currentStep.value = 2;
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.value.email)) {
    formErrors.value.email = 'Please enter a valid email address';
    currentStep.value = 2;
    return;
  }

  if (!validatePhoneNumber(formData.value.phone)) {
    formErrors.value.phone = 'Please enter a valid phone number (minimum 8 digits)';
    currentStep.value = 2;
    return;
  }

  try {
    isSubmitting.value = true;

    const { documentId, companyId } = props;

    const contactData = {
      ...formData.value,
      timestamp: new Date(),
      cardShareId: props.cardShareId,
      status: 'pending'
    };

    const contactsCollection = collection(db, `${FIREBASE_COLLECTIONS.SMART_CARDS}/${documentId}/${FIREBASE_SUBCOLLECTIONS.RECEIVED_CONTACTS}`);
    const docRef = await addDoc(contactsCollection, contactData);

    // Track event
    trackEvent(documentId, 'contact_received', {}, companyId);

    if (props.fcmToken) {
      // Validate FCM token format
      if (typeof props.fcmToken !== 'string' || !props.fcmToken.trim()) {
        console.error('Invalid FCM token format:', props.fcmToken);
        throw new Error('Invalid FCM token format');
      }

      const token = props.fcmToken.trim();
      console.log('FCM Token validation:', {
        token: token,
        length: token.length,
        isString: typeof token === 'string'
      });

      try {
        const notificationData = {
          token: token,
          title: 'New Contact Request',
          body: `${formData.value.firstName} ${formData.value.lastName} wants to share their contact information with you.`,
          data: {
            type: 'contact_request',
            contactId: docRef.id,
            documentId: documentId
          }
        };

        console.log('Sending notification with data:', {
          token: `${token.substring(0, 10)}...${token.substring(token.length - 10)}`,
          title: notificationData.title,
          body: notificationData.body,
          data: notificationData.data
        });

        // Send notification using Cloud Functions
        const result = await sendNotification(notificationData);
        console.log('Notification result:', result);
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
        // Continue with form submission even if notification fails
      }
    } else {
      console.log('No FCM token available, skipping notification');
    }

    emit('submit-success', { ...contactData, id: docRef.id });

    // Reset form
    formData.value = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      title: '',
      timestamp: new Date()
    };

    closeSheet();
  } catch (error) {
    console.error('Error submitting contact:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    emit('submit-error', errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (__props.show)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "bottom-sheet-overlay",
        onClick: _withModifiers(closeSheet, ["self"])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _cache[6] || (_cache[6] = _createElementVNode("h2", null, "Share Your Contact Information", -1)),
              _createElementVNode("p", _hoisted_4, " Would you like to share your contact information with " + _toDisplayString(__props.recipientName) + "? They will be able to add you to their contacts. ", 1)
            ]),
            _createElementVNode("div", _hoisted_5, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (step) => {
                return _createElementVNode("div", {
                  key: step,
                  class: _normalizeClass(['step-dot', { active: currentStep.value === step }])
                }, null, 2)
              }), 64))
            ]),
            _createElementVNode("form", {
              onSubmit: _withModifiers(handleSubmit, ["prevent"]),
              class: "contact-form"
            }, [
              (currentStep.value === 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, [
                        _cache[7] || (_cache[7] = _createElementVNode("label", { for: "firstName" }, "First name*", -1)),
                        _withDirectives(_createElementVNode("input", {
                          id: "firstName",
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.firstName) = $event)),
                          type: "text",
                          required: "",
                          placeholder: "First name",
                          class: _normalizeClass(['form-input', { 'error': formErrors.value.firstName }])
                        }, null, 2), [
                          [_vModelText, formData.value.firstName]
                        ]),
                        (formErrors.value.firstName)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(formErrors.value.firstName), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_10, [
                        _cache[8] || (_cache[8] = _createElementVNode("label", { for: "lastName" }, "Last name*", -1)),
                        _withDirectives(_createElementVNode("input", {
                          id: "lastName",
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.lastName) = $event)),
                          type: "text",
                          required: "",
                          placeholder: "Last name",
                          class: _normalizeClass(['form-input', { 'error': formErrors.value.lastName }])
                        }, null, 2), [
                          [_vModelText, formData.value.lastName]
                        ]),
                        (formErrors.value.lastName)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(formErrors.value.lastName), 1))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true),
              (currentStep.value === 2)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _cache[9] || (_cache[9] = _createElementVNode("label", { for: "email" }, "Work email*", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "email",
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.email) = $event)),
                        type: "email",
                        required: "",
                        placeholder: "you@company.com",
                        class: _normalizeClass(['form-input', { 'error': formErrors.value.email }])
                      }, null, 2), [
                        [_vModelText, formData.value.email]
                      ]),
                      (formErrors.value.email)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(formErrors.value.email), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      _cache[10] || (_cache[10] = _createElementVNode("label", { for: "phone" }, "Work phone*", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "phone",
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.phone) = $event)),
                        type: "tel",
                        required: "",
                        placeholder: "+1 (555) 555-5555",
                        class: _normalizeClass(['form-input', { 'error': formErrors.value.phone }])
                      }, null, 2), [
                        [_vModelText, formData.value.phone]
                      ]),
                      (formErrors.value.phone)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(formErrors.value.phone), 1))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                : _createCommentVNode("", true),
              (currentStep.value === 3)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    _createElementVNode("div", _hoisted_18, [
                      _cache[11] || (_cache[11] = _createElementVNode("label", { for: "company" }, "Company name", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "company",
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.company) = $event)),
                        type: "text",
                        placeholder: "Enter your company name",
                        class: "form-input"
                      }, null, 512), [
                        [_vModelText, formData.value.company]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_19, [
                      _cache[12] || (_cache[12] = _createElementVNode("label", { for: "title" }, "Job title", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "title",
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.title) = $event)),
                        type: "text",
                        placeholder: "Enter your job title",
                        class: "form-input"
                      }, null, 512), [
                        [_vModelText, formData.value.title]
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("button", {
                  type: "button",
                  class: "skip-btn",
                  onClick: closeSheet
                }, " Skip for now "),
                (currentStep.value > 1)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "back-btn",
                      onClick: previousStep
                    }, " Back "))
                  : _createCommentVNode("", true),
                (currentStep.value < 3)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      type: "button",
                      class: "next-btn",
                      onClick: nextStep
                    }, " Next "))
                  : _createCommentVNode("", true),
                (currentStep.value === 3)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 2,
                      type: "submit",
                      class: "submit-btn",
                      disabled: isSubmitting.value
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: isSubmitting.value ? 'line-md:loading-twotone-loop' : 'mdi:send',
                        width: "20",
                        height: "20"
                      }, null, 8, ["icon"]),
                      _createTextVNode(" " + _toDisplayString(isSubmitting.value ? 'Sending...' : 'Send'), 1)
                    ], 8, _hoisted_21))
                  : _createCommentVNode("", true)
              ])
            ], 32)
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})